import api from '../../services/api';

export interface Category { CategoryID: number; Code: string; Name: string; IsActive: boolean; SortOrder: number; }
export interface AccountMatch {
  AccountNumber: string; SchoolName?: string; StudentName?: string; SignerName?: string;
  StudentStatus?: string; CourseName?: string; ContractDate?: string; MatchedOn?: string;
}
export interface Call {
  CallID: number; ZoomCallId?: string; AgentID?: number; AgentName?: string; Direction?: string;
  CallerNumber?: string; CalleeNumber?: string; CallTimeUtc: string;
  CallStartTimeUtc?: string; CallAnswerTimeUtc?: string; DurationSeconds?: number;
  RecordingUrl?: string; AccountNumber?: string; SchoolName?: string; StudentName?: string;
  StudentStatus?: string; CourseName?: string; ContractDate?: string; MatchSource?: string;
  IsMissed: boolean;
  Notes?: string; Status: string; Source: string; Categories: Category[];
  ZoomUserId?: string;
}
// Only the account link + agent-entered annotations are editable; account details are derived.
export interface CallInput {
  AccountNumber?: string; MatchSource?: string; Notes?: string;
  Status: string; CallTimeUtc?: string; CallerNumber?: string; CategoryIds: number[];
}
export interface AgentAnsweredStat { AgentID: number; DisplayName?: string; AnsweredCount: number; }
export interface MissedByHourStat { Hour: number; MissedCount: number; }
export interface DailyCallStat { Date: string; AnsweredCount: number; MissedCount: number; }
export interface RecentMissedCall {
  CallID: number; ZoomCallId?: string; CallerNumber?: string; CallTimeUtc: string;
  AccountNumber?: string; SchoolName?: string; StudentName?: string;
}
// A call can carry multiple categories, so category counts sum to assignments, not calls.
export interface CategoryCallStat { CategoryID: number; Code: string; Name: string; CallCount: number; }
export interface MissedCallMetrics {
  AnsweredCount: number; MissedCount: number;
  PerAgent: AgentAnsweredStat[]; MissedByHour: MissedByHourStat[]; RecentMissed: RecentMissedCall[];
  ByDay: DailyCallStat[]; ByCategory: CategoryCallStat[];
}
export interface Agent {
  AgentID: number; AzureADObjectID?: string; ZoomUserId?: string; ZoomEmail?: string; IsActive: boolean;
  DisplayName?: string; Email?: string; ZoomDisplayName?: string;
  JcsaEmployeeID?: number; JcsaEmployeeUnassigned?: boolean;
  MatchedJcsaEmployeeID?: number; MatchedJcsaEmployeeName?: string;
}
export interface JcsaEmployee { EmployeeID: number; Name?: string; Email?: string; }
export interface CurrentAgent { AgentID?: number; IsMapped: boolean; IsAdmin: boolean; DisplayName: string; Environment: string; }
export interface ZoomUser { UserId: string; Email?: string; DisplayName?: string; }
export interface CommunicationSettings {
  ZoomCallQueueExtension?: string;
  ZoomCallQueueExtensionSource: 'Database' | 'Config' | 'Unset';
  UpdatedUtc?: string;
  UpdatedBy?: string;
}
export interface ZoomQueuePreview {
  Found: boolean; Extension: string; QueueId?: string; QueueName?: string; MemberCount: number;
}
// One Sunday-start week of Workforce hours/FTE metrics (CS Cost Reduction project).
export interface WeeklyMetrics {
  WeekStartDate: string; WeekEndDate: string;
  ScheduledHours: number; WorkedHours: number; AnsweringRoleWorkedHours: number;
  RecommendedHours: number; ScheduledFTE: number; WorkedFTE: number; AnsweringRoleWorkedFTE: number;
  BaselineHours: number; BaselineFTE: number;
  WorkedVsBaselinePct: number; WorkedVsRecommendedHours: number;
}
export interface RosterMember {
  RosterID: number; CanonicalName: string; Role: string; TimeZone: string;
  IsActive: boolean; LoadedAtUtc: string;
}

export const getMe = () => api.get<CurrentAgent>('/communication/me').then(r => r.data);
export const getCategories = () => api.get<Category[]>('/communication/categories').then(r => r.data);
export const getMyCalls = (status?: string) =>
  api.get<Call[]>('/communication/calls', { params: { status } }).then(r => r.data);
export const getCall = (id: number) => api.get<Call>(`/communication/calls/${id}`).then(r => r.data);
export const createCall = (input: CallInput) =>
  api.post<{ callId: number }>('/communication/calls', input).then(r => r.data);
export const updateCall = (id: number, input: CallInput) =>
  api.put(`/communication/calls/${id}`, input).then(r => r.data);
export const lookupAccount = (accountNumber: string) =>
  api.get<AccountMatch | null>('/communication/account-lookup', { params: { accountNumber } }).then(r => r.data);
export const lookupByPhone = (phone: string) =>
  api.get<AccountMatch[]>('/communication/account-lookup/by-phone', { params: { phone } }).then(r => r.data);

export const getAgents = () => api.get<Agent[]>('/communication/admin/agents').then(r => r.data);
export const deactivateAgent = (id: number) => api.delete(`/communication/admin/agents/${id}`).then(r => r.data);
export const reactivateAgent = (id: number) => api.post(`/communication/admin/agents/${id}/reactivate`).then(r => r.data);
export const getAllCalls = (status?: string) =>
  api.get<Call[]>('/communication/admin/calls', { params: { status } }).then(r => r.data);
export const getUnmappedCalls = () => api.get<Call[]>('/communication/admin/unmapped-calls').then(r => r.data);
// from/to are inclusive US Eastern calendar dates (YYYY-MM-DD); the API converts to UTC.
export const getMissedMetrics = (from: string, to: string) =>
  api.get<MissedCallMetrics>('/communication/admin/missed-metrics', { params: { from, to } }).then(r => r.data);
export const upsertCategory = (c: Partial<Category>) =>
  api.post<Category>('/communication/admin/categories', c).then(r => r.data);
export const deactivateCategory = (id: number) => api.delete(`/communication/admin/categories/${id}`).then(r => r.data);

export const getZoomUsers = () => api.get<ZoomUser[]>('/communication/admin/zoom-users').then(r => r.data);
export const setAgentZoom = (agentId: number, body: { ZoomUserId?: string; ZoomEmail?: string; ZoomDisplayName?: string }) =>
  api.post<Agent>('/communication/admin/agents/' + agentId + '/zoom', body).then(r => r.data);
export const setAgentJcsaEmployee = (agentId: number, jcsaEmployeeId: number | null, unassigned = false) =>
  api.post<Agent>('/communication/admin/agents/' + agentId + '/jcsa-employee', { JcsaEmployeeID: jcsaEmployeeId, Unassigned: unassigned }).then(r => r.data);
export const getJcsaEmployees = () =>
  api.get<JcsaEmployee[]>('/communication/admin/jcsa-employees').then(r => r.data);
export const assignCall = (callId: number, agentId: number) =>
  api.post('/communication/admin/calls/' + callId + '/assign', { AgentId: agentId }).then(r => r.data);
export const simulateCall = (body: { CallerNumber?: string; ZoomUserId?: string; Missed?: boolean }) =>
  api.post('/communication/dev/simulate-call', body).then(r => r.data);

export const getTimeTrackingWeeklyMetrics = (weeks = 26) =>
  api.get<WeeklyMetrics[]>('/communication/admin/timetracking/weekly-metrics', { params: { weeks } }).then(r => r.data);
export const getTimeTrackingRoster = () =>
  api.get<RosterMember[]>('/communication/admin/timetracking/roster').then(r => r.data);

export const getSettings = () =>
  api.get<CommunicationSettings>('/communication/admin/settings').then(r => r.data);
export const updateSettings = (body: { ZoomCallQueueExtension?: string }) =>
  api.put<CommunicationSettings>('/communication/admin/settings', body).then(r => r.data);
export const previewZoomQueue = (extension: string) =>
  api.get<ZoomQueuePreview>('/communication/admin/settings/zoom-queue-preview', { params: { extension } }).then(r => r.data);
